import React, { useState } from 'react';
import { Loader2, Lock, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAdminAuth } from './AdminAuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';

const AdminSettings = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;
  const { user, changePassword } = useAdminAuth();

  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: null, msg: '' }); // success | error

  const submit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      setStatus({ type: 'error', msg: t.passwordsMismatch });
      return;
    }
    setSaving(true);
    setStatus({ type: null, msg: '' });
    const res = await changePassword(oldPw, newPw);
    setSaving(false);
    if (res.ok) {
      setStatus({ type: 'success', msg: t.passwordChanged });
      setOldPw(''); setNewPw(''); setConfirmPw('');
    } else {
      setStatus({ type: 'error', msg: res.error });
    }
  };

  const txt = isAr ? 'text-right' : 'text-left';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className={`text-2xl md:text-3xl font-bold text-slate-900 dark:text-white ${txt}`}>{t.settings}</h1>

      <div className={`rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 ${txt}`}>
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t.welcome}</p>
        <p className="text-base font-bold text-slate-900 dark:text-white">{user?.email}</p>
        <p className="text-xs text-slate-500 mt-1">Role: <span className="font-mono">{user?.role}</span></p>
      </div>

      <form onSubmit={submit} className={`rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 space-y-4 ${txt}`}>
        <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Lock className="w-4 h-4 text-brand-blue" />
          {t.changePasswordTitle}
        </h2>

        <div>
          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">{t.oldPassword}</label>
          <input type="password" required value={oldPw} onChange={(e) => setOldPw(e.target.value)} className={inputCls} data-testid="settings-old-pw" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">{t.newPassword}</label>
          <input type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inputCls} data-testid="settings-new-pw" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">{t.confirmPassword}</label>
          <input type="password" required minLength={8} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className={inputCls} data-testid="settings-confirm-pw" />
        </div>

        {status.type && (
          <div
            data-testid="settings-status"
            className={`flex items-start gap-2 px-4 py-3 rounded-xl border text-sm ${
              status.type === 'success'
                ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-400/30 text-emerald-700 dark:text-emerald-300'
                : 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-400/30 text-red-700 dark:text-red-300'
            }`}
          >
            {status.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
            <span>{status.msg}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={saving || !oldPw || !newPw || !confirmPw}
          data-testid="settings-submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow hover:shadow-lg disabled:opacity-60"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {t.save}
        </button>
      </form>
    </div>
  );
};

const inputCls =
  'w-full px-3 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

export default AdminSettings;
